<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, ref, watch } from 'vue'

  // Internal imports

  import { useI18n } from 'vue-i18n'

  import { LoginEvents, Screens, type ScreenChangeData } from '../types'

  import ForgotPassword from './forgot-password/ForgotPassword.vue'

  import ProceedToLogin from './forgot-password/ProceedToLogin.vue'

  import ExpiredPassword from './login/ExpiredPassword.vue'

  import LoginOtpInput from './login/LoginOtpInput.vue'

  import LoginWithEmailPassword from './login/LoginWithEmailPassword.vue'

  import LoginWithSecureCode from './login/LoginWithSecureCode.vue'

  import CompleteSignup from './login/CompleteSignup.vue'

  import ProfileConfirmation from './login/ProfileConfirmation.vue'

  import SetPassword from './login/SetPassword.vue'

  import SignUp from './signup/SignUp.vue'

  // Props definitions
  const props = defineProps({
    page: {
      type: String,
      required: true,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
    /**
     * Passed to migration-gate screens (ExpiredPassword, ProfileConfirmation) that embed branding.
     * Full-page chrome (logo row, back, step titles) is owned by the host layout (e.g. clientportal AuthPageLayout), not this handler.
     */
    showBranding: {
      type: Boolean,
      default: false,
    },
    /** Portal logo for embedded migration-gate screens when showBranding is true */
    portalLogo: {
      type: String as PropType<string | null>,
      default: null,
    },
    /** Portal name for embedded migration-gate screens when showBranding is true */
    portalName: {
      type: String,
      default: 'ClientPortal',
    },
    /**
     * Allows host apps (e.g. clientportal) to disable SSO for a build/tenant.
     */
    isSsoDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, OTP and password logins without a verified password open Set Password
     * before the session is saved. Google sign-in ignores this flag.
     */
    deferSetPasswordSession: {
      type: Boolean,
      default: false,
    },
    /**
     * Route-driven hosts (full-page auth) supply this to mark screen changes that
     * the host handles via a route navigation. For those, AuthHandler skips its
     * internal screen swap and only emits the change — the route remount brings in
     * the new screen, so the page transition plays once instead of an instant
     * pre-swap followed by a re-animated navigation.
     *
     * Modal hosts (login modal, Gokollab) omit this → AuthHandler swaps in place
     * for every change, exactly as before.
     */
    shouldDeferScreenChange: {
      type: Function as PropType<(fromScreen: string, toScreen: string) => boolean>,
      default: null,
    },
    /**
     * When false, LoginOtpInput hides "Change email" (magic-link OTP handoff).
     * Default true preserves login-with-code / modal OTP exits.
     */
    showChangeEmail: {
      type: Boolean,
      default: true,
    },
  })

  // Emits definitions
  const emit = defineEmits([LoginEvents.CHANGE_SCREEN, LoginEvents.LOGIN_CALLBACK])

  // Refs and reactive variables

  const screen = ref({
    email: globalThis?.history?.state?.email || '',
    username: '',
    password: '',
    otpToken: globalThis?.history?.state?.token || '',
    otp: globalThis?.history?.state?.otp || '',
    page: props.page || Screens.LOGIN,
    multiUser: globalThis?.history?.state?.multiUser || [],
    resetPassword: globalThis?.history?.state?.resetPassword || false,
    passwordResetToken: globalThis?.history?.state?.passwordResetToken || '',
    contactId: globalThis?.history?.state?.contactId || '',
    fullName: '',
    referrerUser: undefined,
  } as ScreenChangeData)

  // Composables
  const { t } = useI18n()

  // Computed properties
  const title = computed(() => {
    if (screen.value.page === Screens.SIGN_UP) {
      return t('auth.titles.signUp')
    }
    if (screen.value.page === Screens.COMPLETE_SIGNUP) {
      return t('auth.titles.signUp')
    }
    if (screen.value.page === Screens.VERIFY_SECURE_CODE) {
      return t('auth.titles.enterOtp')
    }
    if (screen.value.page === Screens.SELECT_USER_MULTI_USER) {
      return t('auth.titles.selectUser')
    }
    if (screen.value.page === Screens.FORGOT_PASSWORD) {
      return t('auth.titles.resetPassword')
    }
    if (screen.value.page === Screens.LOGIN_WITH_EMAIL) {
      return t('auth.titles.login')
    }
    if (screen.value.page === Screens.EXPIRED_PASSWORD) {
      return t('auth.titles.expiredPassword')
    }
    if (screen.value.page === Screens.PROFILE_CONFIRMATION) {
      return t('auth.titles.profileConfirmation')
    }

    return t('auth.titles.login')
  })

  // Sync screen.page with props.page when parent updates (e.g. header back or store)
  watch(
    () => props.page,
    newPage => {
      if (newPage && screen.value.page !== newPage) {
        screen.value = { ...screen.value, page: newPage }
      }
    }
  )

  // User-defined functions
  interface AuthEvent {
    eventType: string
    token?: string
    user?: {
      id?: string
      email?: string
      fullName?: string
    }
  }

  async function callback(event: AuthEvent) {
    emit(LoginEvents.LOGIN_CALLBACK, event)
    if (event.eventType == LoginEvents.LOGIN_SUCCESSFUL) {
      // await useCurrentUser().ensureLoaded()
    }
  }

  function onChangeScreen(val: ScreenChangeData) {
    const next = { ...screen.value, ...val }

    // Route-driven change: let the host navigate and remount with the new screen.
    // Skip the internal swap so the current screen stays put until the route
    // transition brings the new page in — avoids the appear-then-navigate double.
    if (val.page && props.shouldDeferScreenChange?.(screen.value.page, val.page)) {
      emit(LoginEvents.CHANGE_SCREEN, next)
      return
    }

    screen.value = next
    emit(LoginEvents.CHANGE_SCREEN, screen.value)
  }
</script>

<template>
  <div class="w-full">
    <LoginWithEmailPassword
      v-if="screen.page === Screens.LOGIN"
      :referrer-user="props.referrerUser"
      :email="screen.email"
      :is-sso-disabled="props.isSsoDisabled"
      :defer-set-password-session="props.deferSetPasswordSession"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <!-- Generate Secure code form -->
    <LoginWithSecureCode
      v-else-if="screen.page === Screens.LOGIN_WITH_SECURE_CODE"
      :email="screen.email"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />
    <!-- Login with otp -->
    <LoginOtpInput
      v-else-if="screen.page === Screens.VERIFY_SECURE_CODE"
      :email="screen.email"
      :otp-token="screen.otpToken"
      :password="screen.password"
      :username="screen.username"
      :default-otp="screen.otp"
      :action="screen.action"
      :is-new-user="screen.isNewUser"
      :referrer-user="screen.referrerUser"
      :defer-set-password-session="props.deferSetPasswordSession"
      :show-change-email="props.showChangeEmail"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <!-- Complete signup after OTP for unknown users on login path -->
    <CompleteSignup
      v-else-if="screen.page === Screens.COMPLETE_SIGNUP"
      :email="screen.email"
      :otp="screen.otp"
      :otp-token="screen.otpToken"
      :referrer-user="screen.referrerUser"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <SetPassword
      v-if="screen.page === Screens.SET_PASSWORD"
      :email="screen.email"
      :contact-id="screen.contactId || ''"
      :password-reset-token="screen.passwordResetToken || ''"
      :persist-session-after-set-password="props.deferSetPasswordSession"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <ForgotPassword
      v-if="screen.page === Screens.FORGOT_PASSWORD"
      :email="screen.email"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <ProceedToLogin
      v-if="screen.page === Screens.PROCEED_TO_LOGIN"
      :email="screen.email"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <SignUp
      v-if="screen.page === Screens.SIGN_UP"
      :referrer-user="props.referrerUser"
      :email="screen.email"
      :is-sso-disabled="props.isSsoDisabled"
      :defer-set-password-session="props.deferSetPasswordSession"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />

    <!-- Migration gate: account's password expired / never set. Wraps SetPassword. -->
    <ExpiredPassword
      v-if="screen.page === Screens.EXPIRED_PASSWORD"
      :show-branding="props.showBranding"
      :portal-logo="props.portalLogo"
      :portal-name="props.portalName"
    />

    <!-- Migration gate: confirm profile (full name) before continuing. -->
    <ProfileConfirmation
      v-if="screen.page === Screens.PROFILE_CONFIRMATION"
      :show-branding="props.showBranding"
      :portal-logo="props.portalLogo"
      :portal-name="props.portalName"
      @changeScreen="onChangeScreen"
      @loginCallback="callback"
    />
  </div>
</template>
